import * as React from 'react';
import * as moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  addCalendarEntryRequest,
  deleteCalendarEntryRequest,
  setDayComment,
  setDayScore,
} from '../../../store/calendar';
import HappinessDayNote from './happinessDayNote';
import { DateFormat } from '../../../../common/utils/dateUtils';
import { Card } from '@mui/material';
import './hapinessCalendar.less';
import { happinessScoreMarks } from './happinessScore';
import Calendar  from './calendar';
import { isDateAfterToday, normalizeDate } from '../utils';

interface HappinessCalendarProps {
  username: string;
  view: 'monthly' | 'yearly';
  handleDisplayedDateChange: (date: moment.Moment) => void;
  handleDisplayedYearChange: (year: number) => void;
}

export function HappinessCalendar(props: HappinessCalendarProps) {
  const { username, view, handleDisplayedDateChange, handleDisplayedYearChange } = props;

  const dispatch = useDispatch();

  const isConnected = useSelector(
    (state: RootState) => state.metadata.isWsAlive
  );
  const { dayScore, dayComment, isBusy, displayedDate, displayedYear, entries } = useSelector((state: RootState) => state.calendar);
  const loggedInUser = useSelector((state: RootState) => state.login.username);
  const formattedDisplayedDate = moment(
    displayedDate,
    DateFormat.LEGACY_MOMENT_SHORT_ISO
  );
  const userData = useSelector((state: RootState) =>
    state.calendar.entries.filter((e) => e.username === username)
  );

  const belongsToUser = useMemo(() => username === loggedInUser, [username, loggedInUser]);
  const ratingInfo = useMemo(() => userData.find((data) => data.date.includes(displayedDate)), [userData, displayedDate]);

  const handleDayCommentChange = (value: string) => dispatch(setDayComment(value));
  const handleDayScoreChange =  (value: number) => dispatch(setDayScore(value));
  const handleDelete = () => dispatch(deleteCalendarEntryRequest(ratingInfo));
  const handleSave = () => dispatch(addCalendarEntryRequest());

  const handleDatePicked = useCallback((date: moment.Moment) => {
    const normalizedDate = normalizeDate(date);
    if (!isDateAfterToday(normalizedDate)) {
      handleDisplayedDateChange(normalizedDate);
      handleDisplayedYearChange(normalizedDate.year());
    }
  }, [normalizeDate, isDateAfterToday, handleDisplayedDateChange, handleDisplayedYearChange]);

  const isDisabled = (date: moment.Moment) => isDateAfterToday(normalizeDate(date));

  const canEdit =
    belongsToUser &&
    !isDateAfterToday(formattedDisplayedDate) &&
    !ratingInfo;
  const canDelete =
    belongsToUser &&
    !isDateAfterToday(formattedDisplayedDate) &&
    ratingInfo?.dateEntered &&
    moment
      .duration(moment().utc().diff(moment(ratingInfo.dateEntered)))
      .asHours() <= 1;

  const getDayColor = useCallback((date: moment.Moment) => {
    const normalizedDate = normalizeDate(date);
    const dateString = normalizedDate.toJSON();
    const info = userData.find((s) => dateString.includes(s.date));
    if (!info) {
      return;
    }
    switch (info.score) {
      case 0:
        return 'lightcoral';
      case 1:
        return 'lightpink';
      case 2:
        return 'peachpuff';
      case 3:
        return 'palegoldenrod';
      case 4:
        return 'palegreen';
      case 5:
        return 'lightgreen';
    }
  }, [normalizeDate, userData]);

  const getDayIcon = useCallback((date: moment.Moment) => {
    const dateString = normalizeDate(date).toJSON();
    const info = userData.find((s) => dateString.includes(s.date));
    return info ? info?.icon ?? happinessScoreMarks[info.score].label : undefined;
  }, [normalizeDate, userData]);

  const handleYearChange = useCallback((year: moment.Moment) => {
    handleDisplayedYearChange(year.year());
  }, [handleDisplayedYearChange]);

  return (
    <Card className='calendarContainer' variant='outlined'>
      <Calendar
        display={view}
        displayedDate={displayedDate}
        displayedYear={displayedYear}
        onYearChange={handleYearChange}
        onChange={handleDatePicked}
        isDisabled={isDisabled}
        getDayColor={getDayColor}
        getDayIcon={getDayIcon}
        shouldDisableYear={
          (year: moment.Moment) => {
            const yearsSet = new Set(entries.map(item => moment(item.date).year()));
            const availableYears = Array.from(yearsSet);
            return !availableYears.includes(year.year());
          }
        }
      />
      <HappinessDayNote dayColor={getDayColor(formattedDisplayedDate)}
                        dayComment={dayComment}
                        dayScore={dayScore}
                        displayedDate={formattedDisplayedDate}
                        ratingInfo={ratingInfo}
                        belongsToUser={belongsToUser}
                        isConnected={isConnected}
                        isBusy={isBusy}
                        canEdit={canEdit}
                        canDelete={canDelete}
                        dayIcon={getDayIcon(formattedDisplayedDate)}
                        handleCommentChange={handleDayCommentChange}
                        handleScoreChange={handleDayScoreChange}
                        handleSave={handleSave}
                        handleDelete={handleDelete} />
    </Card>
  );
}